// Money for both languages (round2-i18n.md §6 "Money"). Amounts arrive in MINOR
// units (the wire format, DB-14a); the currency symbol and minor-unit scale come
// from the settings registry (API-27) and are passed in — never hardcoded (INF-1).
//
//   formatMoney('en', 150_000_00, { currencySymbol: s })                 → s + "1,50,000"
//   formatMoney('en', 150_000_00, { currencySymbol: s, compact: true })  → s + "1.5 L"
//   formatMoney('bn', 150_000_00, { currencySymbol: s, compact: true })  → s + "১.৫ লাখ"
//
// COMPACTION — DC00 decision 30 is OPEN. The designs disagree:
//   customer `money()`: ≥1 crore → "1.5 Cr", ≥1 lakh → "1.5 L", 2 dp, trailing zeros trimmed
//   merchant  `money()`: ≥1 crore → "1.5 cr", ≥1 lakh → "1.5 lakh", fixed 1 dp
//   admin    `moneyStr()`: en fixed 2 dp "Cr"/"L"; bn " কোটি"/" লাখ"
// Until decision 30 closes, the CUSTOMER rule is the default (compactDecimals 2,
// trimZeros true, unitStyle 'short'). The merchant rule is expressible as
// { compactDecimals: 1, trimZeros: false, unitStyle: 'long' }. The unit words are
// dictionary keys (format.money.*), so bn always reads লাখ/কোটি.
//
// Negative amounts: "−" (U+2212) before the symbol, as in the customer design.
// A symbol ending in a letter (a currency CODE fallback) gets a space before the
// amount so it never runs into the digits.
import type { Lang } from './translate';
import { t } from './translate';
import type { Grouping } from './format-number';
import { MINUS, formatNumberAscii, toBanglaDigits } from './format-number';
import { interpolate } from './interpolate';

export const ONE_LAKH = 100_000;
export const ONE_CRORE = 10_000_000;

export interface MoneyOptions {
  currencySymbol: string; // from settings (currency.symbol ?? currency.code)
  minorUnitScale?: number; // minor units per major unit; default 100 (settings currency.minor_unit_scale)
  compact?: boolean; // lakh/crore compaction; default false
  compactDecimals?: number; // decision 30: default 2 (customer rule)
  trimZeros?: boolean; // decision 30: default true ("1.5 L", not "1.50 L")
  unitStyle?: 'short' | 'long'; // en "L"/"Cr" (default) vs "lakh"/"crore"; bn is লাখ/কোটি either way
  grouping?: Grouping; // default 'south-asian' (see format-number.ts)
}

export function minorToMajor(minorUnits: number, minorUnitScale = 100): number {
  return minorUnits / minorUnitScale;
}

function fractionDigitsOf(scale: number): number {
  return Math.max(0, Math.round(Math.log10(scale)));
}

export function formatMoney(lang: Lang, minorUnits: number, opts: MoneyOptions): string {
  const {
    currencySymbol,
    minorUnitScale = 100,
    compact = false,
    compactDecimals = 2,
    trimZeros = true,
    unitStyle = 'short',
    grouping = 'south-asian',
  } = opts;
  const major = minorToMajor(minorUnits, minorUnitScale);
  const abs = Math.abs(major);
  const localise = (s: string) => (lang === 'bn' ? toBanglaDigits(s) : s);

  let amount: string;
  let isZero: boolean;
  const compacted = compact ? compactParts(abs, compactDecimals, trimZeros) : null;
  if (compacted) {
    const unitKey =
      compacted.unit === 'crore'
        ? unitStyle === 'long'
          ? 'format.money.crore'
          : 'format.money.crore_short'
        : unitStyle === 'long'
          ? 'format.money.lakh'
          : 'format.money.lakh_short';
    amount = interpolate(lang, t(lang, 'format.money.compact'), {
      amount: localise(compacted.value),
      unit: t(lang, unitKey),
    });
    isZero = false;
  } else {
    // Whole amounts show no decimals ("1,200"); fractional ones show the
    // currency's full minor precision ("1,200.50").
    const digits = Number.isInteger(abs) ? 0 : fractionDigitsOf(minorUnitScale);
    const ascii = formatNumberAscii(abs, {
      grouping,
      maxFractionDigits: digits,
      minFractionDigits: digits,
    });
    amount = localise(ascii);
    isZero = !/[1-9]/.test(ascii);
  }

  const sep = /[A-Za-z]$/.test(currencySymbol) ? ' ' : '';
  const sign = major < 0 && !isZero ? MINUS : '';
  return `${sign}${currencySymbol}${sep}${amount}`;
}

interface CompactParts {
  value: string; // ASCII digits
  unit: 'lakh' | 'crore';
}

// null below one lakh (the plain grouped amount is used instead).
function compactParts(abs: number, decimals: number, trimZeros: boolean): CompactParts | null {
  if (abs < ONE_LAKH) return null;
  const fmt = (x: number) =>
    formatNumberAscii(x, {
      grouping: 'south-asian',
      maxFractionDigits: decimals,
      minFractionDigits: trimZeros ? 0 : decimals,
    });
  if (abs >= ONE_CRORE) return { value: fmt(abs / ONE_CRORE), unit: 'crore' };
  // 99,99,999 rounds to "100 L" at 2 dp — promote it to "1 Cr" instead.
  const factor = 10 ** decimals;
  if (Math.round((abs / ONE_LAKH) * factor) / factor >= 100) {
    return { value: fmt(1), unit: 'crore' };
  }
  return { value: fmt(abs / ONE_LAKH), unit: 'lakh' };
}
